<template>
  <Teleport to="body">
    <ClientOnly>
      <transition name="modalSlideUp">
        <div
          v-if="props.isOpen"
          aria-label="Get availability alerts"
          aria-modal="true"
          class="fixed inset-0 z-inpage-modal flex items-end justify-center bg-black-transparent-60 md:items-center"
          role="dialog"
          @click.self="props.onClose"
        >
          <div ref="panelRef" class="modalSlideUp-panel max-h-[90dvh] w-full max-w-[560px] overflow-y-auto bg-white font-Spectral">
            <div class="relative px-6 py-8 md:px-10">
              <button ref="closeButtonRef" aria-label="Close" class="absolute right-1 top-1 h-10 w-10 p-2" data-testid="waitlist-registration-modal-close" type="button" @click="props.onClose">
                <img alt="" class="h-full w-full" src="@/assets/icon/close/black.svg" />
              </button>
              <AnimatedContentSwap>
                <WaitlistRegistrationThankYouMessage v-if="hasRegistered" />
                <div v-else>
                  <h2 class="mb-2 text-lg tracking-wide md:text-xl">Get availability alerts</h2>
                  <WaitlistRegistrationForm :experience-id="props.experienceId" :experience-name="props.experienceName" :on-success="onRegistered" />
                </div>
              </AnimatedContentSwap>
            </div>
          </div>
        </div>
      </transition>
    </ClientOnly>
  </Teleport>
</template>

<script lang="ts" setup>
import { onKeyStroke, useScrollLock } from '@vueuse/core';

import AnimatedContentSwap from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/AnimatedContentSwap/AnimatedContentSwap.vue';
import WaitlistRegistrationThankYouMessage from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/WaitlistRegistrationThankYouMessage/WaitlistRegistrationThankYouMessage.vue';
import WaitlistRegistrationForm from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/WaitlistRegistrationForm/WaitlistRegistrationForm.vue';

interface Props {
  isOpen: boolean;
  experienceId: string;
  experienceName: string;
  onClose: () => void;
}

const props = defineProps<Props>();

// ResponsiveDrawer は PC 側が radix Dialog だが、アニメーション用の tailwindcss-animate が未導入で
// 開閉が一瞬で切り替わる。検索モーダルと同じ fade で見せるため、ここでは自前のモーダルにする
const bodyElementRef = ref<HTMLElement | null>(null);
onMounted(() => {
  bodyElementRef.value = document.body;
});
const isBackgroundLocked = useScrollLock(bodyElementRef);
const hasRegistered = ref(false);

const panelRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<HTMLElement | null>(null);
const lastFocusedElement = ref<HTMLElement | null>(null);

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const getFocusableElements = (): HTMLElement[] => (panelRef.value ? Array.from(panelRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : []);

watch(
  () => props.isOpen,
  async (isOpen) => {
    isBackgroundLocked.value = isOpen;
    if (isOpen) {
      // 閉じる側でフォームに戻すと、フェードアウト中に完了表示が入れ替わって見える
      hasRegistered.value = false;
      lastFocusedElement.value = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      await nextTick();
      closeButtonRef.value?.focus();
      return;
    }
    // aria-modal で背景を不活性と伝えている以上、閉じたあとのフォーカスは呼び出し元へ戻す
    lastFocusedElement.value?.focus();
    lastFocusedElement.value = null;
  },
);
onBeforeUnmount(() => {
  isBackgroundLocked.value = false;
});

const onRegistered = async () => {
  hasRegistered.value = true;
  // 送信ボタンごと差し替わるため、focus を戻さないと body に落ちてキーボードから抜け出せなくなる
  await nextTick();
  closeButtonRef.value?.focus();
};

onKeyStroke('Escape', () => {
  if (props.isOpen) props.onClose();
});

onKeyStroke('Tab', (event) => {
  if (!props.isOpen) return;
  const focusables = getFocusableElements();
  if (focusables.length === 0) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  const isInsidePanel = active instanceof Node && panelRef.value?.contains(active);

  if (event.shiftKey && (active === first || !isInsidePanel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !isInsidePanel)) {
    event.preventDefault();
    first.focus();
  }
});
</script>
